import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
import {
  getLocalSources,
  createLocalSource,
  updateLocalSource,
  deleteLocalSource,
  testRssFeed,
} from '../api/localSources';
import toast from 'react-hot-toast';
import {
  Plus,
  Pencil,
  Trash2,
  TestTube,
  X,
  Check,
  Loader2,
  MapPin,
  Rss,
  Globe,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  url: '',
  rssUrl: '',
  country: '',
  state: '',
  district: '',
  city: '',
  taluk: '',
  language: 'en',
  keywords: '',
  priority: 5,
  isActive: true,
};

const LocalSourcesDashboard = () => {
  const { adminToken } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getLocalSources(adminToken);
      if (response.success) {
        setSources(response.sources || []);
      }
    } catch (error) {
      console.error('Error fetching local sources:', error);
      toast.error('Failed to load local sources');
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) fetchSources();
  }, [adminToken, fetchSources]);

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowForm(false);
    setTestResult(null);
  };

  const handleEdit = (source) => {
    setForm({
      name: source.name || '',
      url: source.url || '',
      rssUrl: source.rssUrl || '',
      country: source.country || '',
      state: source.state || '',
      district: source.district || '',
      city: source.city || '',
      taluk: source.taluk || '',
      language: source.language || 'en',
      keywords: Array.isArray(source.keywords) ? source.keywords.join(', ') : '',
      priority: source.priority || 5,
      isActive: source.isActive !== false,
    });
    setEditingId(source._id);
    setShowForm(true);
    setTestResult(null);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete source "${name}"? This cannot be undone.`)) return;
    try {
      await deleteLocalSource(id, adminToken);
      toast.success(`Deleted "${name}"`);
      fetchSources();
    } catch (error) {
      console.error('Error deleting source:', error);
      toast.error('Failed to delete source');
    }
  };

  const handleTestRss = async (rssUrl) => {
    if (!rssUrl) {
      toast.error('Enter an RSS URL first');
      return;
    }
    try {
      setTesting(rssUrl);
      setTestResult(null);
      const result = await testRssFeed(rssUrl, adminToken);
      setTestResult(result);
      if (result.success) {
        toast.success(`RSS feed OK: ${result.itemCount} articles found`);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'RSS test failed';
      setTestResult({ success: false, error: msg });
      toast.error(msg);
    } finally {
      setTesting(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.rssUrl.trim()) {
      toast.error('Name and RSS URL are required');
      return;
    }

    const payload = {
      ...form,
      keywords: form.keywords
        ? form.keywords.split(',').map((k) => k.trim()).filter(Boolean)
        : [],
      priority: parseInt(form.priority, 10) || 5,
    };

    try {
      setSaving(true);
      if (editingId) {
        await updateLocalSource(editingId, payload, adminToken);
        toast.success(`Updated "${form.name}"`);
      } else {
        await createLocalSource(payload, adminToken);
        toast.success(`Added "${form.name}"`);
      }
      resetForm();
      fetchSources();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to save source';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (source) => {
    try {
      await updateLocalSource(source._id, { isActive: !source.isActive }, adminToken);
      toast.success(`${source.name} ${source.isActive ? 'disabled' : 'enabled'}`);
      fetchSources();
    } catch (error) {
      toast.error('Failed to toggle source');
    }
  };

  const filteredSources = sources.filter((s) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.country || '').toLowerCase().includes(q) ||
      (s.state || '').toLowerCase().includes(q) ||
      (s.district || '').toLowerCase().includes(q) ||
      (s.city || '').toLowerCase().includes(q) ||
      (s.rssUrl || '').toLowerCase().includes(q)
    );
  });

  const locationLabel = (s) =>
    [s.city, s.taluk, s.district, s.state, s.country].filter(Boolean).join(', ') || 'Global';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Local News Sources
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage RSS feeds from local newspapers. Sources are matched to users by their geographic location.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-500/30"
        >
          <Plus className="w-4 h-4" />
          Add Source
        </button>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white/50 dark:bg-gray-700/50 rounded-xl p-3 border border-white/20 dark:border-gray-600/30">
          <p className="text-xs text-gray-500 dark:text-gray-400">Total Sources</p>
          <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{sources.length}</p>
        </div>
        <div className="bg-white/50 dark:bg-gray-700/50 rounded-xl p-3 border border-white/20 dark:border-gray-600/30">
          <p className="text-xs text-gray-500 dark:text-gray-400">Active</p>
          <p className="text-2xl font-bold text-green-600">{sources.filter((s) => s.isActive).length}</p>
        </div>
        <div className="bg-white/50 dark:bg-gray-700/50 rounded-xl p-3 border border-white/20 dark:border-gray-600/30">
          <p className="text-xs text-gray-500 dark:text-gray-400">Countries</p>
          <p className="text-2xl font-bold text-blue-600">
            {new Set(sources.map((s) => s.country).filter(Boolean)).size}
          </p>
        </div>
        <div className="bg-white/50 dark:bg-gray-700/50 rounded-xl p-3 border border-white/20 dark:border-gray-600/30">
          <p className="text-xs text-gray-500 dark:text-gray-400">With Errors</p>
          <p className="text-2xl font-bold text-red-500">
            {sources.filter((s) => s.lastFetchError).length}
          </p>
        </div>
      </div>

      {/* Search Filter */}
      <div className="relative">
        <input
          type="text"
          placeholder="Filter sources by name, location, URL..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full px-4 py-2.5 pl-10 rounded-xl bg-white/50 dark:bg-gray-700/50 border border-white/20 dark:border-gray-600/30 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/50"
        />
        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                {editingId ? 'Edit Source' : 'Add New Source'}
              </h3>
              <button onClick={resetForm} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Source Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Source Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Suddi Mahithi"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Website URL
                  </label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={(e) => handleInputChange('url', e.target.value)}
                    placeholder="https://www.suddimahithi.com/"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                  />
                </div>
              </div>

              {/* RSS URL + Test */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  RSS Feed URL *
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={form.rssUrl}
                    onChange={(e) => handleInputChange('rssUrl', e.target.value)}
                    placeholder="https://www.suddimahithi.com/feed/"
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => handleTestRss(form.rssUrl)}
                    disabled={!!testing || !form.rssUrl}
                    className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm whitespace-nowrap"
                  >
                    {testing === form.rssUrl ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4" />
                    )}
                    Test
                  </button>
                </div>
                {testResult && (
                  <div
                    className={`mt-2 p-3 rounded-lg text-sm ${
                      testResult.success
                        ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                    }`}
                  >
                    {testResult.success ? (
                      <div>
                        <p className="font-medium flex items-center gap-1">
                          <Check className="w-4 h-4" /> Feed OK ÔÇö {testResult.feedTitle} ({testResult.itemCount} articles)
                        </p>
                        {testResult.sampleItems?.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {testResult.sampleItems.map((item, i) => (
                              <li key={i} className="text-xs truncate">
                                &bull; {item.title}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <p>Failed: {testResult.error || testResult.message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Geographic Coverage */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> Geographic Coverage
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Articles from this source will appear for users in this location. Leave fields blank for broader coverage.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['country', 'state', 'district', 'city', 'taluk'].map((field) => (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5 capitalize">
                        {field}
                      </label>
                      <input
                        type="text"
                        value={form[field]}
                        onChange={(e) => handleInputChange(field, e.target.value)}
                        placeholder={
                          field === 'country'
                            ? 'India'
                            : field === 'state'
                              ? 'Karnataka'
                              : field === 'district'
                                ? 'Dakshina Kannada'
                                : field === 'city'
                                  ? 'Kadaba'
                                  : 'Kadaba'
                        }
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Language, Keywords, Priority */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Language
                  </label>
                  <select
                    value={form.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                  >
                    <option value="en">English</option>
                    <option value="kn">Kannada (Ó▓òÓ▓¿Ó│ìÓ▓¿Ó▓í)</option>
                    <option value="hi">Hindi (Óñ╣Óñ┐Óñ¿ÓÑìÓñªÓÑÇ)</option>
                    <option value="ta">Tamil (Ó«ñÓ««Ó«┐Ó«┤Ó»ì)</option>
                    <option value="te">Telugu (Ó░ñÓ▒åÓ░▓Ó▒üÓ░ùÓ▒ü)</option>
                    <option value="ml">Malayalam (Ó┤«Ó┤▓Ó┤»Ó┤¥Ó┤│Ó┤é)</option>
                    <option value="mr">Marathi (Óñ«Óñ░Óñ¥ÓñáÓÑÇ)</option>
                    <option value="bn">Bengali (Óª¼Óª¥ÓªéÓª▓Óª¥)</option>
                    <option value="gu">Gujarati (Ó¬ùÓ½üÓ¬£Ó¬░Ó¬¥Ó¬ñÓ½Ç)</option>
                    <option value="pa">Punjabi (Ó¿¬Ó®░Ó¿£Ó¿¥Ó¿¼Ó®Ç)</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Keywords (comma separated)
                  </label>
                  <input
                    type="text"
                    value={form.keywords}
                    onChange={(e) => handleInputChange('keywords', e.target.value)}
                    placeholder="Ó▓òÓ▓íÓ▓¼, kadaba, Ó▓ªÓ▓òÓ│ìÓ▓ÀÓ▓┐Ó▓ú Ó▓òÓ▓¿Ó│ìÓ▓¿Ó▓í"
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority (1-10)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-red-500/50 focus:outline-none"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('isActive', !form.isActive)}
                  className="flex items-center gap-2"
                >
                  {form.isActive ? (
                    <ToggleRight className="w-8 h-8 text-green-500" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-gray-400" />
                  )}
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {form.isActive ? 'Active' : 'Disabled'}
                  </span>
                </button>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition shadow-lg shadow-red-500/30"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingId ? 'Update' : 'Add Source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sources List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : filteredSources.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {sources.length === 0 ? (
            <div>
              <Rss className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No local sources yet</p>
              <p className="text-sm mt-1">Add RSS feeds from local newspapers to show them in the Local News section.</p>
            </div>
          ) : (
            <p>No sources match your filter.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSources.map((source) => (
            <div
              key={source._id}
              className={`bg-white/50 dark:bg-gray-700/50 rounded-xl border p-4 transition ${
                source.isActive
                  ? 'border-white/20 dark:border-gray-600/30'
                  : 'border-red-200 dark:border-red-800/30 opacity-60'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {source.name}
                    </h4>
                    {!source.isActive && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                        Disabled
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-full uppercase">
                      {source.language || 'en'}
                    </span>
                    <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 dark:bg-gray-600/30 dark:text-gray-400 rounded-full">
                      P{source.priority || 5}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    {locationLabel(source)}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate flex items-center gap-1">
                    <Rss className="w-3 h-3 flex-shrink-0" />
                    {source.rssUrl}
                  </p>
                  {source.lastFetchError && (
                    <p className="text-xs text-red-500 mt-1 truncate" title={source.lastFetchError}>
                      Error: {source.lastFetchError}
                    </p>
                  )}
                  {source.lastFetchedAt && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Last fetched: {new Date(source.lastFetchedAt).toLocaleString()}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleActive(source)}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600/50 transition"
                    title={source.isActive ? 'Disable' : 'Enable'}
                  >
                    {source.isActive ? (
                      <ToggleRight className="w-5 h-5 text-green-500" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => handleTestRss(source.rssUrl)}
                    disabled={!!testing}
                    className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition"
                    title="Test RSS feed"
                  >
                    {testing === source.rssUrl ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    ) : (
                      <TestTube className="w-5 h-5 text-blue-500" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(source)}
                    className="p-2 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/30 transition"
                    title="Edit"
                  >
                    <Pencil className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(source._id, source.name)}
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
              </div>

              {/* Keywords */}
              {source.keywords?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {source.keywords.map((kw, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 rounded-full"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalSourcesDashboard;
